import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface AICard {
  id: string;
  title: string;
  query: string;
  blocks: any[];
  createdAt: string;
  updatedAt: string;
  shop: string;
}

interface AICardContextType {
  cards: AICard[];
  isLoading: boolean;
  loadCards: (shop: string) => void;
  saveCard: (shop: string, card: Omit<AICard, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateCard: (cardId: string, updates: Partial<AICard>) => void;
  deleteCard: (cardId: string, shop?: string) => Promise<void>;
  refreshCard: (cardId: string) => Promise<any>;
}

const AICardContext = createContext<AICardContextType | undefined>(undefined);

export const useAICards = () => {
  const context = useContext(AICardContext);
  if (!context) {
    throw new Error('useAICards must be used within an AICardProvider');
  }
  return context;
};

interface AICardProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'third-ai-cards';

export const AICardProvider: React.FC<AICardProviderProps> = ({ children }) => {
  const [cards, setCards] = useState<AICard[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load cards from localStorage
  const loadCards = useCallback((shop: string) => {
    try {
      setIsLoading(true);
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const allCards: AICard[] = JSON.parse(stored);
        const shopCards = allCards.filter(card => card.shop === shop);
        setCards(shopCards);
      } else {
        setCards([]);
      }
    } catch (error) {
      console.error('Error loading AI cards:', error);
      setCards([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save card to localStorage
  const saveCard = useCallback((shop: string, cardData: Omit<AICard, 'id' | 'createdAt' | 'updatedAt'>): string => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const allCards: AICard[] = stored ? JSON.parse(stored) : [];
      
      const newCard: AICard = {
        ...cardData,
        id: `ai-card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        shop,
      };

      allCards.push(newCard);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allCards));
      
      // Update local state
      setCards(prev => [...prev, newCard]);
      
      return newCard.id;
    } catch (error) {
      console.error('Error saving AI card:', error);
      throw error;
    }
  }, []);

  // Update card
  const updateCard = useCallback((cardId: string, updates: Partial<AICard>) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const allCards: AICard[] = JSON.parse(stored);
      const updatedCards = allCards.map(card => {
        if (card.id === cardId) {
          return {
            ...card,
            ...updates,
            updatedAt: new Date().toISOString(),
          };
        }
        return card;
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedCards));
      
      // Update local state
      setCards(prev => prev.map(card => 
        card.id === cardId 
          ? { ...card, ...updates, updatedAt: new Date().toISOString() }
          : card
      ));
    } catch (error) {
      console.error('Error updating AI card:', error);
    }
  }, []);

  // Delete card
  const deleteCard = useCallback(async (cardId: string, shop?: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const allCards: AICard[] = JSON.parse(stored);
      const cardToDelete = allCards.find(c => c.id === cardId);
      const filteredCards = allCards.filter(card => card.id !== cardId);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredCards));
      
      // Update local state
      setCards(prev => prev.filter(card => card.id !== cardId));

      // If card is on dashboard, remove it from database
      if (shop && cardToDelete) {
        try {
          const { apiPost } = await import('../lib/api');
          // Remove from merchant widgets by updating widget configuration
          // We'll do this by calling updateMerchantWidgets without this card
          // For now, just log - the widget system will handle it on next save
          console.log('AI card deleted from localStorage. Remove from dashboard via Widget Manager if needed.');
        } catch (error) {
          console.error('Error removing card from database:', error);
        }
      }
    } catch (error) {
      console.error('Error deleting AI card:', error);
    }
  }, []);

  // Refresh card by re-running the query
  const refreshCard = useCallback(async (cardId: string): Promise<any> => {
    try {
      // Get card from localStorage to ensure we have the latest
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        throw new Error('Card not found');
      }

      const allCards: AICard[] = JSON.parse(stored);
      const card = allCards.find(c => c.id === cardId);
      if (!card) {
        throw new Error('Card not found');
      }

      // Import API function
      const { apiPost } = await import('../lib/api');
      
      // Re-run the original query
      const response = await apiPost<{ blocks: any[] }>('/ask-netguru', {
        question: card.query,
        shop: card.shop,
      });

      // Update card with new data
      updateCard(cardId, {
        blocks: response.blocks || [],
        updatedAt: new Date().toISOString(),
      });

      return response;
    } catch (error) {
      console.error('Error refreshing AI card:', error);
      throw error;
    }
  }, [updateCard]);

  const value: AICardContextType = {
    cards,
    isLoading,
    loadCards,
    saveCard,
    updateCard,
    deleteCard,
    refreshCard,
  };

  return (
    <AICardContext.Provider value={value}>
      {children}
    </AICardContext.Provider>
  );
};

